
export const vertexShaderSource = `#version 300 es
in vec4 aPosition;
void main() {
    gl_Position = aPosition;
}
`;

export const fragmentShaderSource = `#version 300 es
precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uAudio[8]; 
uniform float uParams[36]; 
uniform float uBayer[16];

out vec4 outColor;

// --- UTILS ---
float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
}

mat2 rot(float a) {
    float s = sin(a), c = cos(a);
    return mat2(c, -s, s, c);
}

// --- PRIMITIVES ---
float sdBox(vec3 p, vec3 b) {
    vec3 q = abs(p) - b;
    return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

float sdTorus(vec3 p, vec2 t) {
    vec2 q = vec2(length(p.xz) - t.x, p.y);
    return length(q) - t.y;
}

float sdOctahedron(vec3 p, float s) {
    p = abs(p);
    return (p.x + p.y + p.z - s) * 0.57735027;
}

float sdCylinder(vec3 p, vec2 h) {
    vec2 d = abs(vec2(length(p.xz), p.y)) - h;
    return min(max(d.x, d.y), 0.0) + length(max(d, 0.0));
}

// --- CORE MAP ---
float map(vec3 p) {
    // 1. FORM PARAMS
    float topology = uParams[0]; // 0-1 range
    float topX = uParams[1];
    float topY = uParams[2];
    float topZ = uParams[3];
    float scale = uParams[4] * 2.0;
    float grid = uParams[5];
    float symmetry = uParams[6];
    float shapeComplexity = uParams[7];
    float wireframe = uParams[8];
    
    // 2. ENERGY PARAMS
    float drive = uParams[9];
    float inertia = uParams[10];
    float impact = uParams[11];
    float oscillation = uParams[12];
    
    // 3. BEHAVIOR PARAMS
    float warp = uParams[13];
    float fold = uParams[14];
    float quantize = uParams[15];
    float resonance = uParams[16];
    float scatter = uParams[17];
    float fractal = uParams[18];
    
    float time = uTime * (0.1 + oscillation);
    float audioForce = uAudio[5] * drive; 
    float transientForce = uAudio[7] * impact;
    
    // Domain Symmetry
    if (symmetry > 0.01) {
        float symAmt = symmetry * 3.0 * (1.0 + uAudio[1] * 0.5); // Bass expands symmetry
        p.xy = abs(p.xy) - symAmt;
        p.xz *= rot(symmetry * time + uAudio[6] * 2.0); // Centroid rotates symmetry
        p.yz = abs(p.yz) - symAmt * 0.5;
    }
    
    // Domain Warping & Twist
    if (warp > 0.01) {
        float twistAmt = warp * 2.0 * (1.0 + uAudio[4]); // high frequencies twist
        p.xz *= rot(p.y * twistAmt + time * 0.5);
        p.x += sin(p.z * 2.0 + time) * warp * audioForce * (1.0 + uAudio[2]); // lowMid
        p.y += cos(p.x * 2.0 - time) * warp * audioForce * (1.0 + uAudio[3]); // highMid
        p.z += sin(p.y * 3.0 + time * 0.5) * warp * transientForce;
    }
    
    // Quantization
    if (quantize > 0.05) {
        float q = 0.5 + (1.0 - quantize) * 15.0;
        q *= 1.0 - (uAudio[7] * 0.5); // Transients reduce quantization (make it blockier)
        p = floor(p * q) / q;
    }
    
    // Lattice / Grid Repetition
    if (grid > 0.05) {
        float gSize = 2.0 + (1.0 - grid) * 12.0;
        gSize *= 1.0 - (uAudio[0] * 0.2); // Sub bass tightens the grid
        p = mod(p + gSize * 0.5, gSize) - gSize * 0.5;
    }
    
    // Recursive Space Folding
    if (fold > 0.05) {
        int iter = int(fold * 6.0);
        for(int i = 0; i < 6; i++) {
            if(i >= iter) break;
            p = abs(p) - 0.5 * (audioForce + 0.1) * (1.0 + uAudio[1]); // Bass expands fold
            p.xy *= rot(time * 0.15 + float(i));
            p.yz *= rot(time * 0.08);
        }
    }
    
    // Scatter
    if (scatter > 0.01) {
        p += (vec3(hash(p.xy), hash(p.yz), hash(p.zx)) - 0.5) * scatter * audioForce * 2.0 * (1.0 + uAudio[3]); // highMid
    }

    // Fractal
    if (fractal > 0.01) {
        float f = 1.0;
        for(int i = 0; i < 3; i++) {
            p += sin(p.yzx * f * 3.0 + time) * fractal * 0.2 * (1.0 + uAudio[4]); // high
            f *= 2.0;
        }
    }

    // Topology Expansion (Modular)
    float sz = 0.8 * scale + audioForce;
    float d = 1e10;
    
    vec3 tp = p * vec3(1.0 + (topX - 0.5) * 2.0, 1.0 + (topY - 0.5) * 2.0, 1.0 + (topZ - 0.5) * 2.0);

    float dSphere = length(tp) - sz;
    float dBox = sdBox(tp, vec3(sz * 0.85));
    float dTorus = sdTorus(tp, vec2(sz, sz * 0.25));
    float dOct = sdOctahedron(tp, sz * 1.2);
    float dCyl = sdCylinder(tp, vec2(sz * 0.6, sz * 1.0));
    
    // Multi-way lerp
    if (topology < 0.2) {
        d = mix(dSphere, dBox, topology * 5.0);
    } else if (topology < 0.4) {
        d = mix(dBox, dTorus, (topology - 0.2) * 5.0);
    } else if (topology < 0.6) {
        d = mix(dTorus, dOct, (topology - 0.4) * 5.0);
    } else if (topology < 0.8) {
        d = mix(dOct, dCyl, (topology - 0.6) * 5.0);
    } else {
        d = mix(dCyl, dSphere, (topology - 0.8) * 5.0);
    }
    
    // Shape Complexity (add high frequency noise to distance field)
    if (shapeComplexity > 0.01) {
        float complexAudio = uAudio[4] * 2.0; // High frequencies
        d += sin(tp.x * 10.0) * sin(tp.y * 10.0) * sin(tp.z * 10.0) * shapeComplexity * 0.1 * (1.0 + complexAudio);
    }
    
    // Wireframe (subtract a slightly smaller version of the shape, but only along edges)
    if (wireframe > 0.01) {
        float edgeD = length(tp) - (sz - wireframe * 0.1 * (1.0 + uAudio[3])); // Reacts to highMid
        d = max(d, -edgeD);
    }
    
    // Resonance modulation
    d += sin(length(p) * 12.0 * resonance - time * 6.0) * 0.06 * resonance * (1.0 + uAudio[5] * 2.0); // Reacts to RMS
    
    return d * 0.55;
}

vec3 getNormal(vec3 p) {
    vec2 e = vec2(0.001, 0);
    return normalize(vec3(
        map(p + e.xyy) - map(p - e.xyy),
        map(p + e.yxy) - map(p - e.yxy),
        map(p + e.yyx) - map(p - e.yyx)
    ));
}

void main() {
    vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / uResolution.y;
    
    float depth = uParams[31];
    vec3 ro = vec3(0, 0, -4.5 - (1.0 - depth) * 6.0);
    vec3 rd = normalize(vec3(uv, 1.3));
    
    float t = 0.0;
    float maxD = 40.0;
    int hit = 0;
    for(int i = 0; i < 75; i++) {
        float h = map(ro + rd * t);
        if(abs(h) < 0.001) { hit = 1; break; }
        if(t > maxD) break;
        t += h;
    }
    
    vec3 color = vec3(0.005); 
    
    if(hit == 1) {
        vec3 p = ro + rd * t;
        vec3 n = getNormal(p);
        
        // Lighting Params
        float ambientL = uParams[19];
        float dirL = uParams[20];
        float pointL = uParams[21];
        float lColorHue = uParams[22];
        float bloom = uParams[23];
        float roughness = uParams[24];
        float metalness = uParams[25];
        float emission = uParams[26];
        
        vec3 lPos = vec3(3.0 * sin(uTime * 0.5), 5.0, -4.0 * cos(uTime * 0.5));
        vec3 lDir = normalize(vec3(1.0, 1.0, -1.0)); // Directional light
        vec3 pLDir = normalize(lPos - p); // Point light
        
        float diffDir = max(0.0, dot(n, lDir)) * dirL * (1.0 + uAudio[6] * 0.5); // Reacts to centroid
        float diffPoint = max(0.0, dot(n, pLDir)) * pointL * (1.0 + uAudio[2] * 0.5); // Reacts to lowMid
        float diff = diffDir + diffPoint + ambientL * 0.2;
        
        float specDir = pow(max(0.0, dot(reflect(-lDir, n), -rd)), 32.0 * (1.0 - roughness));
        float specPoint = pow(max(0.0, dot(reflect(-pLDir, n), -rd)), 32.0 * (1.0 - roughness));
        float spec = (specDir * dirL + specPoint * pointL) * metalness * (1.0 + uAudio[4]); // Reacts to high
        
        float chroma = uParams[28] * 6.2831;
        float color1 = uParams[29] * 6.2831;
        float color2 = uParams[30] * 6.2831;
        
        // Base color influenced by audio and params
        vec3 baseCol = 0.5 + 0.5 * cos(chroma + n.z * 2.5 + vec3(0.0, 1.2, 2.4) + vec3(color1, color2, color1 * color2) + uAudio[6] * 1.5);
        
        // Light color
        vec3 lightCol = 0.5 + 0.5 * cos(lColorHue * 6.2831 + vec3(0.0, 2.0, 4.0));
        
        color = baseCol * diff * lightCol + spec * lightCol + baseCol * emission * uAudio[5];
        color *= (1.0 - t / maxD);
        
        // Bloom
        color += baseCol * bloom * uAudio[7] * 2.0;
    }
    
    // Post-processing Params
    float grain = uParams[27];
    float chromaticAberration = uParams[32];
    float vignette = uParams[33];
    float noiseIntensity = uParams[34];
    
    // Chromatic Aberration
    if (chromaticAberration > 0.01) {
        float caOffset = chromaticAberration * 0.05 * length(uv);
        // Simple approximation by shifting color channels slightly
        // In a real multi-pass setup we'd sample the buffer again
        color.r += caOffset * uAudio[1] * 2.0; // Reacts to bass
        color.b -= caOffset * uAudio[1] * 2.0;
    }
    
    // Vignette
    if (vignette > 0.01) {
        float v = length(uv);
        color *= smoothstep(1.5, 1.5 - vignette * (1.0 + uAudio[0] * 0.5), v); // Reacts to sub
    }
    
    // Dithering Engine
    int x = int(mod(gl_FragCoord.x, 4.0));
    int y = int(mod(gl_FragCoord.y, 4.0));
    float bayer = uBayer[y * 4 + x] - 0.5;
    
    float seed = fract(uTime * 0.002);
    float noise = hash(uv + seed) * noiseIntensity * 0.5 * (1.0 + uAudio[2]); // Reacts to lowMid
    
    color += noise + bayer * grain * 0.2;
    
    outColor = vec4(clamp(color, 0.0, 1.0), 1.0);
}
`;;
